// The one place that absorbs the open PIN contract questions (pin.status naming,
// email failed-vs-pending). GET /igloowork/:clientId/pin -> this shape. When the backend
// confirms an item, change it here — nothing else moves.
export type IglooPinEmailStatus = 'sent' | 'failed' | 'pending' | 'none'

export type IglooPinStatus = 'active' | 'revoked_pending_sync' | 'revoked' | 'none'

export type IglooPin = {
  issuedAt: Date | null
  emailStatus: IglooPinEmailStatus
  status: IglooPinStatus
  revokedAt: Date | null
  hasIglooAccessId: boolean
}

export type RawIglooPin =
  | {
      issuedAt?: string | number | null
      emailSentAt?: string | number | null
      status?: string | null
      emailStatus?: string | null
      revokedAt?: string | number | null
      hasIglooAccessId?: boolean | null
    }
  | null
  | undefined

export const EMPTY_IGLOO_PIN: IglooPin = {
  emailStatus: 'none',
  hasIglooAccessId: true,
  issuedAt: null,
  revokedAt: null,
  status: 'none',
}

const PIN_STATUSES: IglooPinStatus[] = ['active', 'revoked_pending_sync', 'revoked', 'none']
const EMAIL_STATUSES: IglooPinEmailStatus[] = ['sent', 'failed', 'pending', 'none']

const toDate = (value: string | number | null | undefined): Date | null => {
  if (value === null || value === undefined) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const normalizeIglooPin = (raw: RawIglooPin): IglooPin => {
  const issuedAt = toDate(raw?.issuedAt)
  const revokedAt = toDate(raw?.revokedAt)
  const emailSentAt = toDate(raw?.emailSentAt)

  const status: IglooPinStatus = PIN_STATUSES.includes(raw?.status as IglooPinStatus)
    ? (raw?.status as IglooPinStatus)
    : revokedAt
      ? 'revoked_pending_sync'
      : issuedAt
        ? 'active'
        : 'none'

  const emailStatus: IglooPinEmailStatus = EMAIL_STATUSES.includes(
    raw?.emailStatus as IglooPinEmailStatus,
  )
    ? (raw?.emailStatus as IglooPinEmailStatus)
    : emailSentAt
      ? 'sent'
      : issuedAt
        ? 'pending'
        : 'none'

  // Default true: a backend that omits the flag must not make an otherwise-healthy PIN
  // look unrevokable. Only an explicit `false` surfaces the support warning.
  const hasIglooAccessId = raw?.hasIglooAccessId ?? true

  if (status === 'none' && !issuedAt && !revokedAt) return EMPTY_IGLOO_PIN
  return { emailStatus, hasIglooAccessId, issuedAt, revokedAt, status }
}
